import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import SEO from '../components/SEO';
import { Reveal, Eyebrow, MaskedLine } from '../components/Reveal';
import { VALUES, IMAGES } from '../data/content';

const REGIONS = [
  { title: 'Pan-India', desc: 'Research and advisory delivery capabilities across India, with quantitative and qualitative field reach.' },
  { title: 'East & Northeast India', desc: 'A strong operational presence and deep contextual understanding of Eastern and Northeastern markets.' },
  { title: 'Southeast Asia', desc: 'Market research experience across selected Southeast Asian regional markets.' },
];

export default function About() {
  return (
    <>
      <SEO
        title="About | INSIGHTS STRATEGY — A Division of Kalpataru Enterprise"
        description="INSIGHTS STRATEGY is a research-driven consulting and advisory organisation, operating as a division of Kalpataru Enterprise with roots in infrastructure feasibility and seven years of institutional experience."
        path="/about"
      />
      <section className="relative overflow-hidden bg-brand-deep" data-testid="about-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(23,107,58,0.16),transparent_50%)]" />
        <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-44 lg:px-8 lg:pb-28">
          <Eyebrow dark>About Us</Eyebrow>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="about-headline">
            <MaskedLine delay={0.15}>Built on Experience.</MaskedLine>
            <MaskedLine delay={0.3}>Driven by <span className="text-brand-green">Insight.</span></MaskedLine>
          </h1>
        </div>
      </section>

      <section className="bg-white" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-24 lg:grid-cols-2 lg:gap-20 lg:px-8 lg:py-32">
          <div className="flex flex-col justify-center">
            <Reveal><Eyebrow>Our Story</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">From feasibility studies to multidisciplinary advisory.</h2>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-brand-body">
                <p>INSIGHTS STRATEGY operates as a division of Kalpataru Enterprise, drawing on approximately seven years of institutional experience spanning infrastructure feasibility, business research, strategic advisory, market intelligence, and capacity building.</p>
                <p>The organisation's initial work focused on technical and commercial feasibility studies for infrastructure projects involving private-sector clients.</p>
                <p>Over time, these capabilities expanded to encompass research and consulting assignments across markets, emerging technologies, public policy, sustainability, development, and entrepreneurship.</p>
                <p>Today, INSIGHTS STRATEGY aims to deliver multidisciplinary, evidence-based consulting solutions across India and selected international markets.</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full rounded-2xl border-2 border-brand-green/30" aria-hidden="true" />
              <img src={IMAGES.kolkataContext} alt="Kolkata — home of INSIGHTS STRATEGY" className="relative aspect-[4/5] w-full rounded-2xl object-cover sm:aspect-[5/4] lg:aspect-[4/5]" loading="lazy" data-testid="about-story-image" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-offwhite" data-testid="about-leadership">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>Leadership</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">Leadership &amp; Expertise</h2>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            <Reveal>
              <div className="h-full rounded-2xl border border-brand-grey bg-white p-8" data-testid="leadership-founder-card">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-deep font-heading text-2xl font-extrabold text-brand-green" aria-label="Portrait placeholder for Biplab Chakraborty">
                  BC
                </div>
                <h3 className="mt-6 font-heading text-xl font-bold text-brand-black">Mr. Biplab Chakraborty</h3>
                <p className="mt-1 text-sm font-semibold text-brand-deepgreen">Founder &amp; Leadership</p>
                <p className="mt-4 text-sm leading-relaxed text-brand-body">
                  INSIGHTS STRATEGY is led by Mr. Biplab Chakraborty, a former Indian Air Force officer, bringing disciplined leadership and strategic perspective to the organisation's research and advisory work.
                </p>
                <p className="mt-4 text-xs italic leading-relaxed text-brand-body/70">Detailed profile and photograph will be published following approval.</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-2xl border border-brand-grey bg-white p-8" data-testid="leadership-network-card">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-offwhite font-heading text-xs font-extrabold uppercase tracking-widest text-brand-deepgreen">
                  Network
                </div>
                <h3 className="mt-6 font-heading text-xl font-bold text-brand-black">Professional Network</h3>
                <p className="mt-1 text-sm font-semibold text-brand-deepgreen">Advisors &amp; Specialists</p>
                <p className="mt-4 text-sm leading-relaxed text-brand-body">
                  The organisation is supported by an experienced professional network, including former Big Four and MBB consulting professionals, industry specialists, researchers, advisors, and business leaders.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex h-full flex-col justify-between rounded-2xl bg-brand-deep p-8" data-testid="leadership-cta-card">
                <div>
                  <h3 className="font-heading text-xl font-bold text-white">Work with our team</h3>
                  <p className="mt-4 text-sm leading-relaxed text-white/60">
                    Every engagement is led by experienced professionals and supported by rigorous research capability.
                  </p>
                </div>
                <Link to="/contact" data-testid="leadership-cta" className="group mt-8 inline-flex items-center gap-2 font-heading text-sm font-bold text-brand-green">
                  Start a Conversation <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>Our Values</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">Principles that guide every engagement.</h2>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.07 * i}>
                <div className="group h-full rounded-2xl border border-brand-grey bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-green/40 hover:shadow-[0_20px_50px_rgba(11,13,11,0.08)]" data-testid={`value-card-${i}`}>
                  <span className="font-heading text-sm font-extrabold text-brand-green">0{i + 1}</span>
                  <h3 className="mt-4 font-heading text-lg font-bold leading-snug text-brand-black">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-body">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-offwhite" data-testid="about-geography">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>Geographical Presence</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">Where we work.</h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-4 text-base leading-relaxed text-brand-body">
                Headquartered in Kolkata, INSIGHTS STRATEGY delivers research and advisory work across India, with a particularly strong presence in Eastern and Northeastern India and research experience relating to Southeast Asia.
              </p>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {REGIONS.map((r, i) => (
              <Reveal key={r.title} delay={0.08 * i}>
                <div className="h-full rounded-2xl border border-brand-grey bg-white p-7" data-testid={`region-card-${i}`}>
                  <div className="flex items-center gap-2 text-brand-deepgreen">
                    <MapPin size={16} />
                    <h3 className="font-heading text-lg font-bold">{r.title}</h3>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-brand-body">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-deep" data-testid="about-parent-org">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-24 lg:grid-cols-12 lg:px-8 lg:py-32">
          <div className="lg:col-span-4">
            <Reveal><Eyebrow dark>Our Parent Organisation</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">A Division of Kalpataru Enterprise</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.15}>
              <div className="space-y-4 text-base leading-relaxed text-white/65">
                <p>INSIGHTS STRATEGY operates as the consulting and research division of Kalpataru Enterprise. The parent organisation's institutional experience — approximately seven years spanning infrastructure feasibility, business research and advisory work — provides the foundation on which the division builds.</p>
                <p>Experience and credentials attributable to Kalpataru Enterprise are identified as such across this website, and are presented distinctly from engagements delivered under the INSIGHTS STRATEGY brand.</p>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/15 px-5 py-3">
                <span className="h-2 w-2 rounded-full bg-brand-green" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Research</span>
                <span className="h-3 w-px bg-brand-green" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Strategy</span>
                <span className="h-3 w-px bg-brand-green" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Impact</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
